const en = {
  // ── Login Page ──
  login: {
    welcomeBack: 'Welcome Back',
    createAccount: 'Create Account',
    signInTitle: 'Sign in to your Sustainability Control Tower',
    signInDescription: 'Monitor, analyse, and optimise your environmental, energy, safety, and governance performance across all operations — from plant floor to boardroom.',
    registerSubtitle: 'Set up your EESG Control Tower access',
    email: 'Email',
    password: 'Password',
    fullName: 'Full Name',
    role: 'Role',
    signIn: 'Sign In',
    create: 'Create Account',
    pleaseWait: 'Please wait...',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    emailPlaceholder: 'you@company.com',
    passwordPlaceholder: 'Enter password',
    namePlaceholder: 'Enter your name',
    heroTitle: 'EESG Control Tower\n& Digital Twin',
    heroDescription: 'One integrated decision layer for economic performance, environmental stewardship, social responsibility, and governance excellence.',
  },

  // ── Sidebar ──
  sidebar: {
    cockpit: 'Executive Cockpit',
    heatmap: 'Heat Map',
    twin: 'Digital Twin',
    emissions: 'Emission Inventory',
    oee: 'OEE & Sustainability',
    cbam: 'CBAM',
    reports: 'Reports Hub',
    governance: 'Governance',
    alerts: 'Alerts',
    admin: 'Admin Console',
    badge: 'EESG Control Tower',
    systemStatus: 'System Status',
    allOperational: 'All systems operational',
    takeTour: 'Take Tour',
  },

  // ── Header ──
  header: {
    selectIndustry: 'Select Industry',
    signOut: 'Sign Out',
    industries: {
      'Manufacturing': 'Manufacturing',
      'Oil and Gas': 'Oil and Gas',
      'Marine': 'Marine',
      'Built Environment': 'Built Environment',
      'Materials and Construction': 'Materials and Construction',
    },
  },

  // ── Guided Tour ──
  tour: {
    welcomeTitle: 'Welcome to SusTek',
    welcomeSubtitle: 'EESG Control Tower',
    welcomeBody: 'Your integrated command centre for environmental, energy, safety, and governance performance. Let us walk you through the key modules.',
    cockpitTitle: 'Executive Cockpit',
    cockpitBody: 'Your real-time operational dashboard. Monitor KPIs across energy, carbon, water, safety, and cost metrics — with threshold alerts and site drill-downs.',
    heatmapTitle: 'Enterprise Heat Map',
    heatmapBody: 'Visualise performance across all sites and industries at a glance. Spot outliers, compare regions, and drill into any cell for root cause.',
    twinTitle: 'Digital Twin & AI',
    twinBody: 'Run what-if scenarios powered by AI. Test carbon price changes, efficiency improvements, and supply chain shifts — with AI-generated strategic advice.',
    emissionsTitle: 'Emission Inventory',
    emissionsBody: 'Full GHG Protocol reporting for all 7 Kyoto Protocol gases across Scope 1, 2, and 3. Includes boundary definitions and base-year tracking.',
    oeeTitle: 'OEE & Sustainability',
    oeeBody: 'Overall Equipment Effectiveness married with sustainability metrics. Drill-down tiles, AI-powered insights, custom scenario simulator, and portfolio ROI.',
    cbamTitle: 'CBAM Compliance',
    cbamBody: 'EU Carbon Border Adjustment Mechanism module. Track product exposure, supplier compliance, shipment declarations, and generate export packs.',
    reportsTitle: 'Reports Hub',
    reportsBody: 'All your sustainability frameworks in one place — IFRS S2, GRI, SASB, TNFD, CDP, and CBAM. Evidence traceability and smart insights included.',
    industryTitle: 'Industry Selector',
    industryBody: 'Switch between industry verticals instantly. Each industry has its own sites, KPIs, scenarios, and benchmarks tailored to operational context.',
    completeTitle: "You're All Set",
    completeSubtitle: 'Start Exploring',
    completeBody: "You now know the key areas of your Control Tower. Dive in, explore the data, and let AI-powered insights guide your decisions. You can restart this tour anytime from the sidebar.",
    startTour: 'Start Tour',
    skip: 'Skip',
    skipTour: 'Skip tour',
    next: 'Next',
    back: 'Back',
    getStarted: 'Get Started',
  },

  // ── Executive Cockpit ──
  cockpit: {
    title: 'Executive Cockpit',
    subtitle: '{industry} — Real-time EESG performance overview',
    thresholds: 'Thresholds',
    boardPack: 'Board Pack',
    generating: 'Generating...',
    sites: '{count} sites',
    selectSite: 'Select a site to drill down',
    close: 'Close',
    energyCarbon: 'Energy & Carbon Trend',
    energyCarbonSub: '14-day rolling average — {industry}',
    controlStatus: 'Control Status',
    ebitdaRisk: 'EBITDA at Risk',
    ebitdaRiskSub: 'Modeled annualized exposure — {industry}',
    oeeCost: 'OEE & Cost Performance',
    oeeCostSub: 'Daily operational efficiency — {industry}',
    activeScenarios: 'Active Scenarios — {industry}',
    impact: 'Impact',
    costPerUnit: 'Cost per Unit',
    carbonIntensity: 'Carbon Intensity',
    trir: 'TRIR',
    assuranceCoverage: 'Assurance Coverage',
    productionCost: 'Production Cost',
    carbonFootprint: 'Carbon Footprint',
    safety: 'Safety (TRIR)',
    green: 'Green',
    amber: 'Amber',
    red: 'Red',
    energy: 'Energy (MWh)',
    co2: 'CO2 (tCO2e)',
    oeePercent: 'OEE %',
    costUnit: 'Cost/Unit',
  },

  // ── Alerts ──
  alerts: {
    title: 'Alerts & Notifications',
    unread: '{count} unread alert(s) for {industry}',
    markRead: 'Mark as Read',
    read: 'Read',
    noAlerts: 'No alerts for this industry.',
    previouslyRead: 'Previously Read',
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
  },

  // ── Governance ──
  governance: {
    title: 'Governance & Assurance',
    subtitle: 'Controls, evidence management, and compliance tracking',
    allStatuses: 'All',
    open: 'Open',
    inProgress: 'In Progress',
    closed: 'Closed',
    noEvents: 'No control events found.',
    controlEvents: 'Control Events',
    filters: 'Filters',
  },

  // ── Notifications ──
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    clearRead: 'Clear read',
    all: 'All',
    unread: 'Unread',
    critical: 'Critical',
    noNotifications: 'No notifications',
    allCaught: "You're all caught up",
  },

  // ── OEE Module ──
  oee: {
    title: 'OEE & Performance x Sustainability',
    subtitle: 'Plant OEE, energy, carbon, and cost analytics',
    tabs: {
      executive: 'Executive KPIs',
      waterfall: 'OEE Waterfall',
      heatmap: 'Line Heatmap',
      initiatives: 'Initiatives',
      scenarios: 'Scenario Simulator',
      portfolio: 'Portfolio ROI',
      pilot: '90-Day Pilot',
    },
    plantOee: 'Plant OEE Breakdown',
    overallOee: 'Overall OEE',
    availability: 'Availability',
    performance: 'Performance',
    quality: 'Quality',
    goodUnits: 'Good Units Today',
    costGoodUnit: 'Cost / Good Unit',
    energyToday: 'Energy Today',
    co2eGoodUnit: 'CO2e / Good Unit',
    scrapRate: 'Scrap Rate',
    downtimeHours: 'Downtime Hours',
    waterUsage: 'Water Usage',
    safetyIncidents: 'Safety Incidents',
    aiAssist: 'AI Assist',
    drilldown: 'Drilldown',
    customScenario: 'Custom Scenario',
    buildScenario: 'Build Scenario',
    runAi: 'Run AI Analysis',
    analyzing: 'Analysing...',
  },

  // ── Emission Inventory ──
  emissions: {
    title: 'Emission Inventory',
    subtitle: 'GHG Protocol Corporate Standard — 7 Kyoto Protocol Gases',
    tabs: {
      overview: 'Overview',
      scope1: 'Scope 1',
      scope2: 'Scope 2',
      scope3: 'Scope 3',
      gases: '7 Gases',
      boundary: 'Boundary',
    },
    totalEmissions: 'Total tCO2e/yr',
    scope1Label: 'Scope 1',
    scope2Label: 'Scope 2',
    scope3Label: 'Scope 3',
    progressBaseYear: 'Progress vs Base Year ({year})',
    baseYear: 'Base Year',
    current: 'Current',
    vsBase: 'vs Base',
    byScope: 'Emissions by Scope',
    byGas: 'Emissions by Gas (tCO2e)',
    monthlyTrend: 'Monthly Emissions Trend (tCO2e)',
    directEmissions: 'Direct Emissions',
    indirectEmissions: 'Energy Indirect Emissions',
    valueChain: 'Value Chain Emissions',
    gasBreakdown: 'Gas Breakdown',
    boundary: 'Organizational Boundary',
    reportingPeriod: 'Reporting Period',
    target: 'Target',
    verification: 'Verification',
    standards: 'Standards',
    id: 'ID',
    category: 'Category',
    description: 'Description',
    gasesCol: 'Gases',
    total: 'Total',
  },

  // ── CBAM Module ──
  cbam: {
    title: 'CBAM Compliance',
    subtitle: 'EU Carbon Border Adjustment Mechanism',
    tabs: {
      executive: 'Executive',
      products: 'Products',
      suppliers: 'Suppliers',
      shipments: 'Shipments',
      twin: 'Digital Twin',
      compliance: 'Supplier Compliance',
    },
  },

  // ── Reports Hub ──
  reports: {
    title: 'Reports Hub',
    subtitle: 'Sustainability framework disclosures and evidence management',
    search: 'Search reports...',
    askHub: 'Ask the Hub',
    frameworks: 'Frameworks',
    insights: 'Insights',
    evidence: 'Evidence',
    actions: 'Actions',
    allFrameworks: 'All Frameworks',
  },

  // ── Admin Console ──
  admin: {
    title: 'Admin Console',
    subtitle: 'Data pipeline, schema, and system management',
  },

  // ── Common ──
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    download: 'Download',
    export: 'Export',
    filter: 'Filter',
    search: 'Search',
    noData: 'No data available',
    total: 'Total',
    target: 'Target',
    actual: 'Actual',
    status: 'Status',
    date: 'Date',
    type: 'Type',
    output: 'Output',
  },

  // ── Industries (display names) ──
  industries: {
    'Manufacturing': 'Manufacturing',
    'Oil and Gas': 'Oil and Gas',
    'Marine': 'Marine',
    'Built Environment': 'Built Environment',
    'Materials and Construction': 'Materials and Construction',
  },

  // ── Login Industries (footer) ──
  loginIndustries: ['Manufacturing', 'Oil & Gas', 'Marine', 'Built Env.', 'Materials'],
};

export default en;
